<template>
  <div class="bg-white text-slate-600">
    <!-- Fixed Header - No transition -->
    <LandingHeader
      :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'"
      brand-url="/"
      :logo-url="globalConfig.LOGO_THUMBNAIL || ''"
      primary-cta-url="/app/auth/signup"
      :nav-links="navLinks"
    />

    <!-- Main Content - Only this part transitions -->
    <main v-if="!loading" class="overflow-hidden page-content">
      <HeroSection
        v-if="content.hero"
        :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'"
        primary-cta-url="/app/auth/signup"
        secondary-cta-url="/app"
        :demo-url="demoUrl"
        :content="content.hero"
        :images="images"
      />
      <MetricsStrip v-if="content.metrics" :content="content.metrics" />
      <FeatureShowcase v-if="content.features" :content="content.features" />
      <AutomationSection v-if="content.automation" :content="content.automation" />
      <WorkflowSection v-if="content.workflow" :content="content.workflow" />
      <IntegrationsSection v-if="content.integrations" :content="content.integrations" />
      <TestimonialsSection v-if="content.testimonials" :content="content.testimonials" />
      <PricingSection
        v-if="content.pricing"
        primary-cta-url="/app/register"
        :content="content.pricing"
        @request-consultation="openConsultationModal"
      />
      <FaqSection :content="content.faq || {}" />
      <CtaSection
        v-if="content.cta"
        primary-cta-url="/app/auth/signup"
        :support-email="supportEmail"
        :content="content.cta"
        @request-consultation="openConsultationModal"
      />
    </main>

    <div v-else class="flex min-h-screen items-center justify-center">
      <p class="text-slate-500">Loading...</p>
    </div>

    <!-- Fixed Footer - No transition -->
    <LandingFooter
      :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'"
      brand-url="/"
      :logo-url="globalConfig.LOGO_THUMBNAIL || ''"
      :support-email="supportEmail"
      :content="footerData"
    />

    <ConsultationModal v-model="isConsultationOpen" :source="consultationSource" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import { setupScrollAnimations } from '../utils/scrollAnimations';
import LandingHeader from '../components/LandingHeader.vue';
import HeroSection from '../components/HeroSection.vue';
import MetricsStrip from '../components/MetricsStrip.vue';
import FeatureShowcase from '../components/FeatureShowcase.vue';
import AutomationSection from '../components/AutomationSection.vue';
import WorkflowSection from '../components/WorkflowSection.vue';
import IntegrationsSection from '../components/IntegrationsSection.vue';
import TestimonialsSection from '../components/TestimonialsSection.vue';
import PricingSection from '../components/PricingSection.vue';
import FaqSection from '../components/FaqSection.vue';
import CtaSection from '../components/CtaSection.vue';
import LandingFooter from '../components/LandingFooter.vue';
import ConsultationModal from '../components/ConsultationModal.vue';

const store = useStore();

const globalConfig = computed(() => store.getters['home/globalConfig']);
const content = computed(() => store.getters['home/content']);
const footerData = computed(() => store.getters['home/footerData']);
const images = computed(() => store.getters['home/images']);
const loading = computed(() => store.getters['home/loading']);

const supportEmail = computed(() => globalConfig.value.SUPPORT_EMAIL || 'support@hilab.vn');
const demoUrl = computed(() => '/app/accounts/new');

// Function to load widget script
const loadWidgetScript = () => {
  if (!globalConfig.value.WIDGET_TOKEN) return;
  
  // Check if script already loaded
  if (window.hichatSDK) return;
  
  const baseUrl = window.location.origin;
  
  // Set widget settings
  window.hichatSettings = {
    position: "right",
    type: "standard", 
    launcherTitle: "Trò chuyện với chúng tôi"
  };
  
  // Create and load script
  const script = document.createElement('script');
  script.src = `${baseUrl}/packs/js/sdk.js`;
  script.defer = true;
  script.async = true;
  
  script.onload = function() {
    if (window.hichatSDK) {
      window.hichatSDK.run({
        websiteToken: globalConfig.value.WIDGET_TOKEN,
        baseUrl: baseUrl
      });
    }
  };
  
  document.head.appendChild(script);
};

const navLinks = computed(() => [
  { to: '/', label: 'Trang chủ' },
  // { to: '/features', label: 'Tính năng' },
  { to: '/docs', label: 'Hướng dẫn' },
  { to: '/#pricing', label: 'Bảng giá', hash: true }
]);

onMounted(async () => {
  await store.dispatch('locale/initLocale');
  await store.dispatch('home/fetchContentWithCache');

  setupScrollAnimations();
  
  // Load widget script after content is loaded
  await nextTick();
  loadWidgetScript();
});

const isConsultationOpen = ref(false);
const consultationSource = ref('');

const openConsultationModal = source => {
  consultationSource.value = typeof source === 'string' ? source : '';
  isConsultationOpen.value = true;
};
</script>
